import {
  BookOpen,
  Download,
  Eye,
  FileArchive,
  FileSpreadsheet,
  ShieldCheck,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { apiErrorMessage } from "../../../api/client.js";
import { Button } from "../../../design/components/Button.js";
import { Alert, Skeleton } from "../../../design/components/Feedback.js";
import { ResponsiveSheet } from "../../../design/components/ResponsiveSheet.js";
import { StatusBadge } from "../../../design/components/StatusBadge.js";
import {
  api,
  type AbstractBookWorkspace,
  type Conference,
  type ExportDataset,
  type ExportWorkspace,
} from "../../api.js";

type BookJob = AbstractBookWorkspace["jobs"][number];

const exportStateTone = (state: ExportWorkspace["jobs"][number]["state"]) => {
  if (state === "READY") return "success" as const;
  if (state === "FAILED" || state === "CANCELLED") return "danger" as const;
  return "neutral" as const;
};

const bookStateTone = (state: BookJob["state"]) => {
  if (state === "PUBLISHED") return "success" as const;
  if (state === "GENERATED") return "info" as const;
  return "neutral" as const;
};

function displayTime(value: string) {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(new Date(value));
}

function datasetName(workspace: ExportWorkspace, key: ExportDataset["key"]) {
  return workspace.datasets.find((dataset) => dataset.key === key)?.label ?? key.toLocaleLowerCase();
}

export function DocumentsExportsCenter({ conference }: { conference: Conference }) {
  const [exports, setExports] = useState<ExportWorkspace | null>(null);
  const [books, setBooks] = useState<AbstractBookWorkspace | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [exportOpen, setExportOpen] = useState(false);
  const [bookOpen, setBookOpen] = useState(false);
  const [datasetKey, setDatasetKey] = useState<ExportDataset["key"] | "">("");
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [bookTitle, setBookTitle] = useState("");
  const [includeAuthors, setIncludeAuthors] = useState(true);
  const [includeAffiliations, setIncludeAffiliations] = useState(true);
  const [orderBy, setOrderBy] = useState<"REFERENCE" | "TITLE">("REFERENCE");
  const [rightsConfirmed, setRightsConfirmed] = useState(false);
  const [consentStatement, setConsentStatement] = useState("");
  const [publication, setPublication] = useState<{ job: BookJob; action: "publish" | "unpublish" } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const [nextExports, nextBooks] = await Promise.all([
        api.exportWorkspace(conference.id, conference.organization_id),
        api.abstractBookWorkspace(conference.id, conference.organization_id),
      ]);
      setExports(nextExports);
      setBooks(nextBooks);
    } catch {
      setExports(null);
      setBooks(null);
      setError("Documents and exports could not be loaded. Check your connection and try again.");
    } finally {
      setLoading(false);
    }
  }, [conference.id, conference.organization_id]);

  useEffect(() => { void load(); }, [load]);

  const selectedDataset = useMemo(
    () => exports?.datasets.find((dataset) => dataset.key === datasetKey) ?? null,
    [datasetKey, exports],
  );

  const chooseDataset = (dataset: ExportDataset) => {
    setDatasetKey(dataset.key);
    setSelectedFields(dataset.fields.filter((field) => field.recommended).map((field) => field.key));
  };

  const beginExport = () => {
    const dataset = exports?.datasets[0];
    if (!dataset) return;
    chooseDataset(dataset);
    setError("");
    setMessage("");
    setExportOpen(true);
  };

  const generateExport = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!selectedDataset) return;
    if (selectedDataset.fields.length && !selectedFields.length) {
      setError("Choose at least one field for this export.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      const created = await api.requestExport(
        conference.id,
        conference.organization_id,
        selectedDataset.key,
        selectedFields,
      );
      const completed = await api.executeExport(
        conference.id,
        conference.organization_id,
        created.id,
      );
      if (completed.state !== "READY") {
        throw new Error("The export was cancelled because access changed while it was generated.");
      }
      await load();
      setExportOpen(false);
      setMessage(`${selectedDataset.label} export generated. Download it before it expires.`);
    } catch (reason) {
      setError(apiErrorMessage(reason, "The export could not be generated."));
    } finally {
      setBusy(false);
    }
  };

  const beginBook = () => {
    setBookTitle(books?.default_title ?? `${conference.name} — Abstract book`);
    setIncludeAuthors(true);
    setIncludeAffiliations(true);
    setOrderBy("REFERENCE");
    setRightsConfirmed(false);
    setConsentStatement("");
    setError("");
    setMessage("");
    setBookOpen(true);
  };

  const generateBook = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!bookTitle.trim() || !rightsConfirmed || !consentStatement.trim()) {
      setError("Add a title, confirm publication rights, and record the consent basis.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api.createAbstractBook(conference.id, conference.organization_id, {
        title: bookTitle.trim(),
        include_authors: includeAuthors,
        include_affiliations: includeAffiliations,
        order_by: orderBy,
        rights_confirmed: rightsConfirmed,
        consent_statement: consentStatement.trim(),
      });
      await load();
      setBookOpen(false);
      setMessage("Private abstract-book preview generated. Inspect it before publishing.");
    } catch (reason) {
      setError(apiErrorMessage(reason, "The abstract book could not be generated."));
    } finally {
      setBusy(false);
    }
  };

  const changePublication = async () => {
    if (!publication) return;
    setBusy(true);
    setError("");
    try {
      await api.setAbstractBookPublication(
        conference.id,
        conference.organization_id,
        publication.job.id,
        publication.action,
      );
      await load();
      setMessage(
        publication.action === "publish"
          ? "Abstract book published. Its public link is now available."
          : "Abstract book unpublished. The immutable file remains in publication history.",
      );
      setPublication(null);
    } catch (reason) {
      setError(apiErrorMessage(reason, `The abstract book could not be ${publication.action}ed.`));
    } finally {
      setBusy(false);
    }
  };

  const toggleField = (field: string, checked: boolean) => {
    setSelectedFields((current) => checked
      ? [...new Set([...current, field])]
      : current.filter((item) => item !== field));
  };

  return <section className="documents-centre" aria-labelledby="documents-centre-title">
    <header className="documents-centre__header">
      <div><span>Documents & exports</span><h2 id="documents-centre-title">Prepare files people can actually use</h2><p>Generate private previews, publish deliberately, and download permission-scoped data without leaving the conference workspace.</p></div>
      <ShieldCheck size={30} aria-hidden="true" />
    </header>

    {error ? <Alert tone="danger">{error} {!exports && !books ? <Button type="button" variant="secondary" onClick={() => void load()}>Try again</Button> : null}</Alert> : null}
    {message ? <Alert tone="success">{message}</Alert> : null}
    {loading && !exports && !books ? <div className="documents-loading"><Skeleton /><Skeleton /></div> : null}

    {exports && books ? <div className="documents-centre__grid">
      <article className="documents-card">
        <header><div className="documents-card__icon"><BookOpen size={21} aria-hidden="true" /></div><div><h3>Abstract book</h3><p>Build a PDF from accepted, released submission versions.</p></div></header>
        <div className="documents-card__metric"><strong>{books.jobs.length}</strong><span>generated version{books.jobs.length === 1 ? "" : "s"}</span></div>
        <Button type="button" onClick={beginBook}>Prepare abstract book</Button>
        <div className="documents-history" aria-label="Abstract book history">
          {books.jobs.length ? books.jobs.map((job) => <article key={job.id}>
            <div className="documents-history__title"><span><strong>{job.title}</strong><small>Version {job.version} · {job.entry_count} entr{job.entry_count === 1 ? "y" : "ies"}</small></span><StatusBadge tone={bookStateTone(job.state)}>{job.state.toLocaleLowerCase()}</StatusBadge></div>
            <small>Generated {displayTime(job.generated_at)} · checksum {job.sha256.slice(0, 10)}</small>
            <div className="documents-history__actions">
              <a href={job.preview_url} target="_blank" rel="noreferrer" aria-label={`Preview ${job.title}`}><Eye size={15} aria-hidden="true" />Preview</a>
              <a href={job.download_url} aria-label={`Download ${job.title}`}><Download size={15} aria-hidden="true" />Download</a>
              {job.public_url ? <a href={job.public_url} target="_blank" rel="noreferrer" aria-label={`Open public ${job.title}`}>Public PDF</a> : null}
              {books.can_publish && job.state === "GENERATED" ? <Button type="button" variant="secondary" aria-label={`Publish ${job.title}`} onClick={() => setPublication({ job, action: "publish" })}>Publish</Button> : null}
              {books.can_publish && job.state === "PUBLISHED" ? <Button type="button" variant="ghost" aria-label={`Unpublish ${job.title}`} onClick={() => setPublication({ job, action: "unpublish" })}>Unpublish</Button> : null}
            </div>
          </article>) : <div className="documents-empty"><FileArchive size={22} aria-hidden="true" /><span><strong>No abstract book yet</strong><small>Generate a private PDF preview when accepted work is ready.</small></span></div>}
        </div>
      </article>

      <article className="documents-card">
        <header><div className="documents-card__icon"><FileSpreadsheet size={21} aria-hidden="true" /></div><div><h3>Private exports</h3><p>Choose only the dataset and fields needed for the task.</p></div></header>
        <div className="documents-card__metric"><strong>{exports.jobs.filter((job) => job.state === "READY").length}</strong><span>download{exports.jobs.filter((job) => job.state === "READY").length === 1 ? "" : "s"} ready</span></div>
        <Button type="button" onClick={beginExport} disabled={!exports.datasets.length}>Create export</Button>
        <div className="documents-history" aria-label="Export history">
          {exports.jobs.length ? exports.jobs.map((job) => <article key={job.id}>
            <div className="documents-history__title"><span><strong>{datasetName(exports, job.kind)}</strong><small>{job.requested_fields.length ? `${job.requested_fields.length} selected fields` : "Calendar file"}</small></span><StatusBadge tone={exportStateTone(job.state)}>{job.state.toLocaleLowerCase()}</StatusBadge></div>
            <small>{job.manifest.row_count === undefined ? "Generated securely" : `${job.manifest.row_count} rows`} · expires {displayTime(job.expires_at)}</small>
            {job.download_url ? <div className="documents-history__actions"><a href={job.download_url} aria-label={`Download ${datasetName(exports, job.kind).toLocaleLowerCase()} export`}><Download size={15} aria-hidden="true" />Download</a></div> : <p className="documents-history__notice">{job.state === "QUEUED" ? "Generation is waiting to run." : "This file is no longer available. Create a fresh export to continue."}</p>}
          </article>) : <div className="documents-empty"><FileSpreadsheet size={22} aria-hidden="true" /><span><strong>No exports yet</strong><small>Generated files appear here for 24 hours.</small></span></div>}
        </div>
      </article>
    </div> : null}

    <ResponsiveSheet open={exportOpen} title="Create export" layout="modal" onClose={() => !busy && setExportOpen(false)} footer={<><Button type="button" variant="secondary" disabled={busy} onClick={() => setExportOpen(false)}>Cancel</Button><Button type="submit" form="create-export-form" busy={busy}>Generate {selectedDataset?.format ?? "file"}</Button></>}>
      <form id="create-export-form" className="document-form" onSubmit={generateExport}>
        <p>Exports contain only records and fields your current conference role can access. Permission is checked again when you download.</p>
        <label><span>Dataset</span><select value={datasetKey} onChange={(event) => { const dataset = exports?.datasets.find((item) => item.key === event.target.value); if (dataset) chooseDataset(dataset); }}>{exports?.datasets.map((dataset) => <option value={dataset.key} key={dataset.key}>{dataset.label} · {dataset.format}</option>)}</select></label>
        {selectedDataset ? <div className="document-form__dataset"><strong>{selectedDataset.label}</strong><p>{selectedDataset.description}</p></div> : null}
        {selectedDataset?.fields.length ? <fieldset><legend>Fields to include</legend><div className="document-field-grid">{selectedDataset.fields.map((field) => <label key={field.key}><input type="checkbox" checked={selectedFields.includes(field.key)} onChange={(event) => toggleField(field.key, event.target.checked)} /><span>{field.label}</span></label>)}</div></fieldset> : <Alert tone="neutral">The calendar export includes the complete published programme.</Alert>}
        <div className="document-form__notice"><ShieldCheck size={18} aria-hidden="true" /><span><strong>Private, scoped, and temporary</strong><small>Downloads expire after 24 hours and are reauthorized every time.</small></span></div>
      </form>
    </ResponsiveSheet>

    <ResponsiveSheet open={bookOpen} title="Prepare abstract book" layout="modal" onClose={() => !busy && setBookOpen(false)} footer={<><Button type="button" variant="secondary" disabled={busy} onClick={() => setBookOpen(false)}>Cancel</Button><Button type="submit" form="create-book-form" busy={busy}>Generate private preview</Button></>}>
      <form id="create-book-form" className="document-form" onSubmit={generateBook}>
        <p>The PDF pins the exact accepted submission, decision, and configuration versions used to build it.</p>
        <label><span>Book title</span><input value={bookTitle} onChange={(event) => setBookTitle(event.target.value)} required /></label>
        <div className="document-form__row"><label><span>Order entries by</span><select value={orderBy} onChange={(event) => setOrderBy(event.target.value as "REFERENCE" | "TITLE")}><option value="REFERENCE">Submission reference</option><option value="TITLE">Paper title</option></select></label></div>
        <fieldset><legend>Published information</legend><div className="document-field-grid"><label><input type="checkbox" checked={includeAuthors} onChange={(event) => setIncludeAuthors(event.target.checked)} /><span>Include author names</span></label><label><input type="checkbox" checked={includeAffiliations} onChange={(event) => setIncludeAffiliations(event.target.checked)} /><span>Include affiliations</span></label></div></fieldset>
        <label className="document-consent"><input type="checkbox" checked={rightsConfirmed} onChange={(event) => setRightsConfirmed(event.target.checked)} /><span><strong>I confirm publication rights</strong><small>The organizer has authority to publish the selected accepted work.</small></span></label>
        <label><span>Consent statement</span><textarea rows={4} value={consentStatement} onChange={(event) => setConsentStatement(event.target.value)} placeholder="Record the approval, policy, or consent basis used for publication." required /></label>
      </form>
    </ResponsiveSheet>

    <ResponsiveSheet open={Boolean(publication)} title={publication?.action === "publish" ? "Publish abstract book?" : "Unpublish abstract book?"} layout="modal" onClose={() => !busy && setPublication(null)} footer={<><Button type="button" variant="secondary" disabled={busy} onClick={() => setPublication(null)}>Cancel</Button><Button type="button" variant={publication?.action === "unpublish" ? "danger" : "primary"} busy={busy} onClick={() => void changePublication()}>{publication?.action === "publish" ? "Publish PDF" : "Unpublish PDF"}</Button></>}>
      <div className="document-confirm"><BookOpen size={25} aria-hidden="true" /><div><h3>{publication?.job.title}</h3><p>{publication?.action === "publish" ? "This exact immutable PDF will receive a public conference link. Preview it before continuing." : "The public link will stop working. The immutable version remains in the organizer history."}</p></div></div>
    </ResponsiveSheet>
  </section>;
}
